import React from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';

import { Path, PathDocument, PathsDocument } from '@edx/graphql/generated';
import { getStaticApolloClient } from '@edx/helpers/apollo';
import { useAuth } from '@edx/hooks/use-auth';
import { Hero } from '@edx/components/hero';
import { EnrollBanner } from '@edx/components/banners';
import { PathsMenu } from '@edx/components/paths-menu';
import { PathFeed } from '@edx/components/path-feed';

type Props = {
  paths: Path[];
  path: Path;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const { apolloClient } = await getStaticApolloClient();
  const { data } = await apolloClient.query({ query: PathsDocument });

  const paths = data.paths.map(({ slug }: Path) => ({ params: { slug } }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const { apolloClient } = await getStaticApolloClient();

  const pathsQuery = await apolloClient.query({ query: PathsDocument });
  const pathQuery = await apolloClient.query({ query: PathDocument, variables: { slug: context.params?.slug } });

  return {
    props: {
      paths: pathsQuery.data.paths,
      path: pathQuery.data.path,
    },
  };
};

const PathPage: React.FC<Props> = ({ path, paths }) => {
  const { isLoggedOut } = useAuth();

  return (
    <>
      <Head>
        {path.metaTitle && <title key="title">{path.metaTitle}</title>}

        {path.metaDescription && <meta key="description" name="description" content={path.metaDescription} />}

        {/* Open Graph */}
        {path.metaTitle && <meta key="og:title" property="og:title" content={path.metaTitle} />}
        {path.metaDescription && <meta key="og:description" property="og:description" content={path.metaDescription} />}
        {path.openGraphImageUrl && <meta key="og:image" property="og:image" content={path.openGraphImageUrl} />}
      </Head>

      <Hero heading={path.name} preheading="· Itinerarios ·" bgImage={path.images.header} />

      <div className="relative max-w-full md:max-w-6xl mx-auto px-4 md:px-10 pb-20">
        <div className="flex flex-col-reverse lg:flex-row space-x-0 lg:space-x-4">
          <div className="w-full lg:w-1/3 flex-shrink-0 mt-10 md:mt-0">
            <PathsMenu paths={paths} />
          </div>

          <div className="flex-grow md:mt-4 lg:mt-0">
            <PathFeed path={path} />
          </div>
        </div>
      </div>

      {isLoggedOut && <EnrollBanner />}
    </>
  );
};

export default PathPage;
